import { apiWorkflowExecutions } from "@/api/workflows/workflow-execution.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { approvalRequestsKeys } from "@/queries/approval/use-approval-requests.query.ts";
import { employeeSurveyKey } from "@/queries/employees/use-employee-survey.query.ts";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { surveysKeys } from "@/queries/surveys/use-surveys.query.ts";
import { esignKeys } from "@/queries/use-esign.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ApiWorkflowExecutionFilter } from "@/types/workflows/workflow-execution.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const workflowExecutionKeys = createQueryKeys("workflowExecutions", {
  search: (filter: ApiWorkflowExecutionFilter) => ({ queryKey: [filter] }),
  workflows: null,
  details: (executionId: number | undefined) => ({ queryKey: [executionId] }),
  meetings: (executionId: number | undefined) => ({ queryKey: [executionId] }),
});

export const useWorkflowExecutionsSearch = ({
  filter,
  enabled = true,
}: {
  filter: ApiWorkflowExecutionFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    ...workflowExecutionKeys.search(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiWorkflowExecutions.search(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useWorkflowExecutionWorkflows = () => {
  return useQuery({
    ...workflowExecutionKeys.workflows,
    queryFn: async ({ signal }) => {
      const result = await apiWorkflowExecutions.getWorkflows(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useWorkflowExecution = (executionId: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...workflowExecutionKeys.details(executionId),
    enabled: enabled && executionId != undefined,
    queryFn: async ({ signal }) => {
      if (executionId == undefined) throw new ApiPassException("Id is required");
      const result = await apiWorkflowExecutions.getById(executionId, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useWorkflowExecutionMeetings = (
  executionId: number | undefined,
  enabled: boolean = true
) => {
  return useQuery({
    ...workflowExecutionKeys.meetings(executionId),
    enabled: enabled && executionId != undefined,
    queryFn: async ({ signal }) => {
      if (executionId == undefined) throw new ApiPassException("Id is required");
      const result = await apiWorkflowExecutions.getMeetings(executionId, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useCancelWorkflow = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function (executionId: number) {
      const result = await apiWorkflowExecutions.cancelWorkflow(executionId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      // Cancellation cancels approval requests with their unfilled surveys, envelopes and child executions
      void queryClient.invalidateQueries({ queryKey: workflowExecutionKeys._def });
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys._def });
      void queryClient.invalidateQueries({ queryKey: surveysKeys._def });
      void queryClient.invalidateQueries({ queryKey: employeeSurveyKey._def });
      void queryClient.invalidateQueries({ queryKey: approvalRequestsKeys._def });
      void queryClient.invalidateQueries({ queryKey: esignKeys._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
